import { PlusOutlined, SettingOutlined } from '@ant-design/icons';
import { Typography, Spin, Row, Col, Button, Space, Pagination, Empty, message } from 'antd';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { Outlet, useSearchParams } from 'react-router-dom';

import useAPIQuery from '../../../api/useAPIQuery';
import ErrorResult from '../../../components/ErrorResult';
import useTypedNavigate from '../../../hooks/useTypedNavigate';
import useTypedParams from '../../../hooks/useTypedParams';
import { withRouteChange } from '../../../services/APM';
import AddRegistrationTemplateDrawer from './AddRegistrationTemplateDrawer';
import RegistrationTemplateCard from './RegistrationTemplateCard';

const RegistrationTemplates = () => {
  const { t } = useTranslation();
  const navigate = useTypedNavigate();
  const config = useAPIQuery('/configs/tenant-configurations/');
  const { tenant } = useTypedParams({ tenant: String });

  const [searchParams, setSearchParams] = useSearchParams();
  const [addRegisterTemplateDrawerVisible, setAddRegisterTemplateDrawerVisible] = useState(false);

  const page = Number(searchParams.get('page')) || 1;
  const pageSize = Number(searchParams.get('pageSize')) || 5;

  const registrationTemplates = useAPIQuery([
    '/registration-templates/',
    {
      params: {
        page,
        page_size: pageSize,
      },
    },
  ]);

  return (
    <ErrorResult error={registrationTemplates.error || config.error}>
      <Helmet>
        <title>
          {t('Formulários de Inscrição | Configurações')} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <AddRegistrationTemplateDrawer
        open={addRegisterTemplateDrawerVisible}
        onClose={() => setAddRegisterTemplateDrawerVisible(false)}
        onSuccess={() => {
          message.success(t('Formulário de inscrição adicionado!'));
          setAddRegisterTemplateDrawerVisible(false);
        }}
      />
      <Outlet />
      <Spin spinning={registrationTemplates.isLoading || config.isLoading}>
        <Row gutter={[8, 8]}>
          <Col span={14}>
            <Typography.Title level={5}>
              <Trans>Formulários de inscrição</Trans>
            </Typography.Title>
            <Typography.Paragraph>
              <Trans>
                Gerencie os modelos de formulário disponíveis para candidaturas nas suas vagas
              </Trans>
            </Typography.Paragraph>
          </Col>
          <Col span={10}>
            <Row justify="end" gutter={8}>
              <Col>
                <Button
                  icon={<SettingOutlined />}
                  onClick={() =>
                    navigate({
                      pathname:
                        '/:tenant/settings/registration-templates/candidate-source-options/',
                      params: { tenant },
                      search: searchParams,
                    })
                  }
                >
                  {' '}
                  <Trans>Origem do candidato</Trans>
                </Button>
              </Col>
              <Col>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setAddRegisterTemplateDrawerVisible(true)}
                >
                  {' '}
                  <Trans>Novo formulário</Trans>
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
        {registrationTemplates.data?.count !== 0 ? (
          <Space direction="vertical" style={{ width: '100%' }}>
            {registrationTemplates.data?.results.map((registrationTemplate) => (
              <RegistrationTemplateCard
                key={registrationTemplate.id}
                standardTemplate={
                  registrationTemplate.id === config.data?.defaultRegistrationTemplateId
                }
                alwaysRequiredCpf={
                  config.data?.enableVixtingAdmission || config.data?.enableSankhyaAdmission
                }
                registrationTemplate={registrationTemplate}
                onEditClick={() =>
                  navigate({
                    pathname:
                      '/:tenant/settings/registration-templates/:registration_template_id/change/',
                    params: { tenant, registration_template_id: registrationTemplate.id },
                    search: searchParams,
                  })
                }
              />
            ))}
            <Pagination
              style={{ display: 'flex', justifyContent: 'end' }}
              current={page}
              total={registrationTemplates.data?.count || 0}
              pageSize={pageSize}
              onChange={(page, pageSize) => {
                searchParams.set('page', page.toString());
                searchParams.set('pageSize', pageSize.toString());
                setSearchParams(searchParams);
              }}
              pageSizeOptions={[5, 10, 20, 50, 100]}
              showTotal={(total) => t(`Total: {{total}} formulários`, { total })}
            />
          </Space>
        ) : (
          <Empty />
        )}
      </Spin>
    </ErrorResult>
  );
};

export default withRouteChange()(RegistrationTemplates);
